import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import * as React from 'react';
import { useNavigate, useSearchParams, type NavigateOptions } from 'react-router-dom';

import { getPresetDateRange, type CalendarDateRange } from '../components/DateRangePicker';

/**
 * URL-based date range hook.
 * - URL is the only source of truth
 * - No URL params? Default to past6Months
 */

// Hook for panes - URL is source of truth
export const useDateRangeParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlStart = searchParams.get('start');
  const urlEnd = searchParams.get('end');

  // Parse URL params or use default, tracking whether URL params are valid
  const { dateRange, urlParamsValid } = React.useMemo(() => {
    const now = today(getLocalTimeZone());

    if (urlStart && urlEnd) {
      try {
        const start = parseDate(urlStart);
        const end = parseDate(urlEnd);
        const clampedEnd = end.compare(now) > 0 ? now : end;
        if (start.compare(clampedEnd) <= 0) {
          return { dateRange: { start, end: clampedEnd }, urlParamsValid: true };
        }
      } catch {
        // Invalid format, fall through to default
      }
    }

    return { dateRange: getPresetDateRange('past6Months'), urlParamsValid: false };
  }, [urlStart, urlEnd]);

  // On mount: if URL params are missing or invalid, set default
  React.useLayoutEffect(() => {
    if (!urlParamsValid) {
      setSearchParams(
        (params) => {
          params.set('start', dateRange.start.toString());
          params.set('end', dateRange.end.toString());
          return params;
        },
        { replace: true }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Setter: just updates URL
  const setDateRange = React.useCallback(
    (range: CalendarDateRange) => {
      setSearchParams((params) => {
        params.set('start', range.start.toString());
        params.set('end', range.end.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  return [dateRange, setDateRange] as const;
};

// Hook that returns a function to add current date range params to a path (for Link hrefs)
export const usePathWithDateRange = () => {
  const [searchParams] = useSearchParams();

  return React.useCallback(
    (path: string) => {
      const start = searchParams.get('start');
      const end = searchParams.get('end');

      if (start && end) {
        const url = new URL(path, window.location.origin);
        url.searchParams.set('start', start);
        url.searchParams.set('end', end);
        return url.pathname + url.search;
      }

      return path;
    },
    [searchParams]
  );
};

// Hook for navigation that preserves date range params
export const useNavigateWithDateRange = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return React.useCallback(
    (to: string, options?: NavigateOptions) => {
      const start = searchParams.get('start');
      const end = searchParams.get('end');

      if (start && end) {
        const url = new URL(to, window.location.origin);
        url.searchParams.set('start', start);
        url.searchParams.set('end', end);
        void navigate(url.pathname + url.search, options);
      } else {
        void navigate(to, options);
      }
    },
    [navigate, searchParams]
  );
};
